import React, { useEffect, useState } from "react";
import defaultLogo from "assets/Logo_New.png";
import rightLogo from "assets/principle_connect_logo.jpg";
import { AccountIcon } from "./AccountIcon";
import { useSelector } from "react-redux";
import { selectBrandLogo } from "reducers/brandSettings";
import { selectSignedIn } from "reducers/auth";
import { MessageIcon } from "./MessageIcon";
import { NotificationIcon } from "./NotificationIcon";
import { ShoppingCartIcon } from "./ShoppingCartIcon";
import { HeaderAction } from "App";
import { MdMenu } from "react-icons/md";
import { useLoadScopes } from "helpers/auth";
import { hasNestedPermission } from "helpers/hasNestedPermission";

type HeaderProps = {
  hideHeader: boolean;
  handleShrinkNavMenu: () => void;
  openMobileNav: () => void;
  setHeaderActions: React.Dispatch<React.SetStateAction<HeaderAction | null>>;
  headerActions: HeaderAction | null;
};

const Header: React.FC<HeaderProps> = ({
  hideHeader,
  handleShrinkNavMenu,
  openMobileNav,
  setHeaderActions,
  headerActions,
}) => {
  const [logo, setLogo] = useState<string | null>(null);

  const brandLogo = useSelector(selectBrandLogo);
  const isSignedIn = useSelector(selectSignedIn);
  const scopes = useLoadScopes();

  // Check if user has access to the shop page
  const hasShopAccess = isSignedIn && scopes.some((scope) => scope.path === "shop");

  // Check if user has access to notifications (home/notifications-list)
  const hasNotificationAccess =
    isSignedIn && hasNestedPermission(scopes, "home/notifications-list");

  useEffect(() => {
    if (brandLogo) {
      if (brandLogo && brandLogo !== logo) {
        setLogo(brandLogo);
      }
    } else {
      setLogo(defaultLogo);
    }
  }, [brandLogo]);

  if (hideHeader) {
    return null;
  }

  const handleMenuClick = () => {
    // Use openMobileNav for screens <= 1200px, handleShrinkNavMenu for larger screens
    if (window.innerWidth <= 1200) {
      openMobileNav();
    } else {
      handleShrinkNavMenu();
    }
  };

  return (
    <div className="Header" data-testid="header">
      <div className="HeaderLeft">
        <div className="NavigationButton" data-testid="header-menu-btn" onClick={handleMenuClick}>
          {MdMenu({ size: "30px" })}
        </div>

        {logo && (
          <div className="LogoContainer" data-testid="header-logo-container">
            <img className="Logo" data-testid="header-logo" src={logo} alt="Logo" />
          </div>
        )}
      </div>

      <div className="HeaderRight">
        {rightLogo && (
          <img
            className="RightLogo"
            data-testid="header-right-logo"
            src={rightLogo}
            alt="Right Logo"
          />
        )}

        <div className="IconsContainer" data-testid="header-icons">
          {hasShopAccess && (
            <div
              className="HeaderAccountIconContainer ShoppingCartIcon"
              data-testid="header-cart-icon"
            >
              <ShoppingCartIcon />
            </div>
          )}
          <div className="HeaderAccountIconContainer MessageIcon" data-testid="header-message-icon">
            <MessageIcon setHeaderActions={setHeaderActions} headerActions={headerActions} />
          </div>
          {hasNotificationAccess && (
            <div
              className="HeaderAccountIconContainer NotificationIcon"
              data-testid="header-notification-icon"
            >
              <NotificationIcon setHeaderActions={setHeaderActions} headerActions={headerActions} />
            </div>
          )}
          <div className="HeaderAccountIconContainer" data-testid="header-account-icon">
            <AccountIcon setHeaderActions={setHeaderActions} headerActions={headerActions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export { Header };
